// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { autocompleteProductsStyles } from "@synd/Overrides/Widgets/Header/AutocompleteProducts";
import LinkArrowRight from "@synd/XDL/Icons/LinkArrowRight";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, mergeWithCss, styledScrollbar, xScreen } from "@synd/XDL/Utilities";
import { css } from "styled-components";

autocompleteProductsStyles.header = {
    css: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
    `,
};

autocompleteProductsStyles.headerText = {
    variant: "h6",
    transform: "uppercase",
    color: "text",
    css: css`
        margin: 0;
    `,
};

autocompleteProductsStyles.headerLink = {
    ...createLinkIconStyles({
        icon: LinkArrowRight,
        linkStyles: {
            color: "primary",
            icon: {
                position: "right",
                iconProps: {
                    width: 8,
                    height: 13,
                    css: css`
                        svg {
                            height: 10px;
                        }
                    `,
                } as any,
            },
            css: css`
                margin-right: 24px;
            `,
        },
        sizeVariant: "xsmall",
    }),
};

autocompleteProductsStyles.wrapper = {
    css: css`
        height: 460px;
        max-height: 65vh;
        ${styledScrollbar()}
        padding-right: 16px;
        padding-bottom: 8px;
        ${xScreen("lte", "md")} {
            height: auto;
            max-height: none;
            padding-right: 0;
            overflow-x: hidden;
            padding-bottom: env(safe-area-inset-bottom);
        }
    `,
};

autocompleteProductsStyles.container = {
    css: css`
        display: grid;
        grid-template-columns: 72px 1fr;
        gap: 8px;
        padding: 8px 0;

        &:first-of-type {
            padding-top: 0;
        }

        &:not(:last-of-type) {
            border-bottom: 1px solid ${color("common.border")};
        }
    `,
};

autocompleteProductsStyles.focusedContainer = {
    ...mergeWithCss(autocompleteProductsStyles.container, {
        css: css`
            background-color: ${color("common.ui2")};
        `,
    }),
};

autocompleteProductsStyles.imageClickable = {
    css: css`
        max-width: 72px;
        max-height: 72px;
        aspect-ratio: 1;
        grid-column: 1 /2;
        grid-row: 1/3;
    `,
};

autocompleteProductsStyles.image = {
    css: css`
        aspect-ratio: 1;
        border-radius: 8px;
        cursor: pointer;
        object-fit: contain;
        width: 100%;
    `,
};

autocompleteProductsStyles.infoGridItem = {
    css: css`
        display: flex;
        flex-direction: column;
        width: 100%;
        row-gap: 2px;
        grid-column: 2/2;
    `,
};

autocompleteProductsStyles.actionsGridItem = {
    css: css`
        display: flex;
        justify-content: space-between;
        grid-column: 2/2;
        column-gap: 16px;

        ${xScreen("lt", "sm")} {
            flex-direction: column;
            row-gap: 8px;
        }
    `,
};

autocompleteProductsStyles.productPriceStyles = {
    price: {
        priceText: {
            ...fontConfig.regularSingleLine,
        },
    },
};

autocompleteProductsStyles.actions = {
    wrapper: {
        css: css`
            width: auto;
            grid-template-columns: minmax(47px, auto) minmax(105px, 1fr);
            align-items: center;
        `,
    },
    viewButton: {
        css: css`
            max-width: fit-content;
            padding: 0 15px;
        `,
    },
    productQuantityOrdered: {
        select: {
            css: css`
                &&&&& {
                    padding: 0 18px 0 6px;
                    text-align: center;
                }
            `,
        },
    },
};

autocompleteProductsStyles.titleLink = {
    color: "text.main",
    typographyProps: {
        ...fontConfig.smallSingleLine,
        weight: fontConfig.weight.bold,
        color: "text.main",
        css: css`
            letter-spacing: 0;
            ${ellipsis(1)};
        `,
    },
};

autocompleteProductsStyles.productTitle = {
    productDetailSubDescription: {
        css: css`
            min-height: auto;
        `,
    },
};
